/**
 * The wall face.
 *
 * One teacher, one glance, from across the room: what time is it, and how long
 * until the next bell. On a primary tablet the large number is how long the
 * current period still has, even when that period does not ring. Everything
 * else on this screen is either a whisper or hidden.
 *
 * Two rules govern the code below.
 *
 *  1. The DOM is built once, in `mountKiosk`. `update()` runs four times a
 *     second for months at a time, so it holds element references and writes
 *     text only when the string it would write has actually changed. No
 *     querySelector, no innerHTML, no re-render.
 *  2. Nothing here reaches the network. Fonts and the mark are local, colours
 *     are literals, and every state — offline, unsynced clock, home day — has a
 *     calm rendering rather than an empty one.
 */

import type { Band, BellEvent, DayPlan, Session } from '../shared/types';

/**
 * Everything the face needs, assembled by `src/main.ts` on every tick. Declared
 * here because this file is the only consumer: the composition root imports the
 * shape from the screen that draws it.
 */
export interface KioskState {
  nowMs: number;
  session: Session;
  plan: DayPlan;
  next: BellEvent | null;
  /**
   * Next period that starts after now, even if it does not ring. Set only on
   * lower-school (K–6) tablets; Upper School keeps the next-bell line alone.
   */
  nextPeriod: Band | null;
  /**
   * Milliseconds left in the period the room is in. Set on lower-school tablets
   * while a period has an end; null on Upper School, during a gap, and once
   * dismissal (which does not end) has started. The bell schedule is unchanged.
   */
  periodLeftMs: number | null;
  /** Name of that period. Empty when `periodLeftMs` is null. */
  periodLabel: string;
  current: Band | null;
  /** Set when today has no more bells, or is a home day. */
  nextSchoolPlan: DayPlan | null;
  /**
   * Display name of this tablet's bell group, e.g. "Primary School". Comes from
   * the published schedule rather than a lookup table here, because the office
   * can create and rename bell groups from the admin portal.
   */
  groupName: string;
  /** Is audio unlocked? */
  armed: boolean;
  online: boolean;
  /** Has the clock synced from the server yet? */
  clockTrusted: boolean;
  note?: string;
}

export interface KioskView {
  update(state: KioskState): void;
  destroy(): void;
}

export interface KioskOptions {
  onArmTap: () => void;
  onTestBell: () => void;
  volume: number;
  onVolume: (v: number) => void;
  onOpenAdmin: () => void;
  onSignOut: () => void;
}

/** The school's zone. The tablets sit in it, but say so rather than assume it. */
const TZ = 'America/Chicago';

/** Five taps on the mark inside this window opens the hidden panel. */
const TAP_COUNT = 5;
const TAP_WINDOW_MS = 3000;

/** The panel is a visitor, not furniture. */
const PANEL_IDLE_MS = 20_000;


/**
 * How long until the next bell, in the fewest words that are still true.
 *
 * Counts UP to the next whole minute the way a timer does, so the line reads
 * "1 min" for the whole of the last minute rather than flicking to "0 min" and
 * sitting there. Inside the final minute it switches to seconds, which is when
 * a teacher actually wants that precision.
 */
function formatUntil(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.ceil(seconds / 60)} min`;
}

const clockFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const weekdayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'long' });

/** '10:14' — the meridiem is dropped; nobody reads a wall clock at midnight. */
function formatClock(ms: number): string {
  let hour = '';
  let minute = '';
  for (const part of clockFormat.formatToParts(new Date(ms))) {
    if (part.type === 'hour') hour = part.value;
    else if (part.type === 'minute') minute = part.value;
  }
  return `${hour}:${minute}`;
}

/** Minutes since local midnight → '8:10'. No timezone maths: the plan is local. */
function formatMinutes(mins: number): string {
  const h24 = Math.floor(mins / 60) % 24;
  const hour = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${hour}:${String(mins % 60).padStart(2, '0')}`;
}

/**
 * 'YYYY-MM-DD' → 'Tuesday'. Built as a local date on purpose: `new Date(iso)`
 * would parse it as UTC midnight and name the day before.
 */
function weekdayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return weekdayFormat.format(new Date(y, m - 1, d));
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * `parent.append(a, b, c)` would be the obvious call, but this project compiles
 * with @cloudflare/workers-types in scope, whose HTMLRewriter `Element.append`
 * merges over the DOM's and only accepts a single string. `appendChild` is
 * unambiguous.
 */
function add(parent: HTMLElement, ...children: HTMLElement[]): void {
  for (const child of children) parent.appendChild(child);
}

export function mountKiosk(root: HTMLElement, opts: KioskOptions): KioskView {
  // ------------------------------------------------------------- the face
  const face = el('div', 'kiosk');

  const mark = el('button', 'kiosk__mark');
  mark.type = 'button';
  mark.setAttribute('aria-label', 'Valor Classical Academy');
  const markImg = document.createElement('img');
  markImg.className = 'kiosk__mark-img';
  // The monogram, not the full lockup. At the size this row can afford without
  // stealing height from the time, the lockup's "CLASSICAL ACADEMY" line renders
  // around 8px — illegible mush on a wall, and below the minimum size the
  // identity guide sets. The guide names the icon as the version for small
  // spaces, so the mark stays crisp and the school is still unmistakably Valor.
  markImg.src = '/brand/valor-icon.png';
  markImg.alt = '';
  markImg.decoding = 'async';
  mark.appendChild(markImg);

  const header = el('header', 'kiosk__top');
  header.appendChild(mark);

  const time = el('div', 'kiosk__time', '—');
  time.setAttribute('aria-live', 'off');
  const rule = el('div', 'kiosk__rule');

  const period = el('p', 'kiosk__period');
  const periodEyebrow = el('span', 'kiosk__period-eyebrow', 'Next period');
  const periodValue = el('span', 'kiosk__period-value', '');
  add(period, periodEyebrow, periodValue);
  period.hidden = true;

  const next = el('p', 'kiosk__next');
  const nextEyebrow = el('span', 'kiosk__next-eyebrow', 'Next bell');
  const nextValue = el('span', 'kiosk__next-value', '');
  const nextLeft = el('span', 'kiosk__next-left', 'left');
  nextLeft.hidden = true;
  const nextCount = el('span', 'kiosk__next-count', '');
  nextCount.hidden = true;
  add(next, nextEyebrow, nextValue, nextLeft, nextCount);

  // Shown only while the large line has been given to the current period, so
  // the next bell stays on the glass and still names nothing but a bell.
  const bell = el('p', 'kiosk__bell');
  const bellEyebrow = el('span', 'kiosk__bell-eyebrow', 'Next bell');
  const bellValue = el('span', 'kiosk__bell-value', '');
  add(bell, bellEyebrow, bellValue);
  bell.hidden = true;

  const note = el('p', 'kiosk__note', '');
  note.hidden = true;

  const centre = el('main', 'kiosk__centre');
  add(centre, time, rule, next, period, bell, note);

  const who = el('p', 'kiosk__who', '');

  const offline = el('span', 'kiosk__flag', 'Offline · bells still ring');
  offline.hidden = true;
  const syncing = el('span', 'kiosk__flag', 'Clock syncing');
  syncing.hidden = true;
  const flags = el('div', 'kiosk__flags');
  add(flags, offline, syncing);

  const footer = el('footer', 'kiosk__bottom');
  add(footer, who, flags);

  add(face, header, centre, footer);

  // ------------------------------------------------------ the hidden panel
  const panel = el('div', 'kiosk__panel');
  panel.hidden = true;

  const volumeRow = el('div', 'kiosk__row');
  const volumeLabel = el('label', 'kiosk__label', 'Volume');
  volumeLabel.htmlFor = 'valor-kiosk-volume';
  const volumeValue = el('span', 'kiosk__label-value', '');
  const volumeHead = el('div', 'kiosk__row-head');
  add(volumeHead, volumeLabel, volumeValue);

  const slider = document.createElement('input');
  slider.className = 'kiosk__slider';
  slider.id = 'valor-kiosk-volume';
  slider.type = 'range';
  slider.min = '0';
  slider.max = '1';
  slider.step = '0.01';
  const startVolume = Math.min(1, Math.max(0, opts.volume));
  slider.value = String(startVolume);
  volumeValue.textContent = `${Math.round(startVolume * 100)}%`;
  add(volumeRow, volumeHead, slider);

  const testBtn = el('button', 'kiosk__btn kiosk__btn--primary', 'Test bell');
  testBtn.type = 'button';
  const adminBtn = el('button', 'kiosk__btn', 'Admin');
  adminBtn.type = 'button';
  const signOutBtn = el('button', 'kiosk__btn', 'Sign out');
  signOutBtn.type = 'button';
  const actions = el('div', 'kiosk__actions');
  add(actions, testBtn, adminBtn, signOutBtn);

  const hint = el('p', 'kiosk__hint', 'Closes itself in a few seconds.');
  add(panel, volumeRow, actions, hint);

  // -------------------------------------------------------- the arm overlay
  const arm = el('div', 'kiosk__arm');
  arm.setAttribute('role', 'button');
  arm.tabIndex = 0;
  const armInner = el('div', 'kiosk__arm-inner');
  const armIcon = document.createElement('img');
  armIcon.className = 'kiosk__arm-icon';
  armIcon.src = '/brand/valor-icon.png';
  armIcon.alt = '';
  armIcon.decoding = 'async';
  const armTitle = el('h1', 'kiosk__arm-title', 'Tap to arm bells');
  const armRule = el('div', 'kiosk__arm-rule');
  const armBody = el(
    'p',
    'kiosk__arm-body',
    'Android keeps the tablet silent until the screen is touched. One tap after a restart and the bells ring for the rest of the day.',
  );
  add(armInner, armIcon, armTitle, armRule, armBody);
  arm.appendChild(armInner);

  add(face, panel, arm);
  root.appendChild(face);

  // ------------------------------------------------------------- behaviour
  let taps: number[] = [];
  let idleTimer: number | undefined;

  function closePanel(): void {
    if (idleTimer !== undefined) {
      window.clearTimeout(idleTimer);
      idleTimer = undefined;
    }
    if (!panel.hidden) panel.hidden = true;
  }

  function keepPanelAlive(): void {
    if (idleTimer !== undefined) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(closePanel, PANEL_IDLE_MS);
  }

  function openPanel(): void {
    panel.hidden = false;
    keepPanelAlive();
  }

  mark.addEventListener('click', () => {
    const t = Date.now();
    taps = taps.filter((stamp) => t - stamp < TAP_WINDOW_MS);
    taps.push(t);
    if (taps.length >= TAP_COUNT) {
      taps = [];
      openPanel();
    }
  });

  // Any touch inside the panel restarts its twenty seconds.
  panel.addEventListener('pointerdown', keepPanelAlive);
  panel.addEventListener('input', keepPanelAlive);

  // A tap anywhere else on the face puts it away again.
  face.addEventListener('pointerdown', (event) => {
    if (panel.hidden) return;
    const target = event.target as Node;
    if (!panel.contains(target) && !mark.contains(target)) closePanel();
  });

  slider.addEventListener('input', () => {
    const v = Number(slider.value);
    volumeValue.textContent = `${Math.round(v * 100)}%`;
    opts.onVolume(v);
  });

  testBtn.addEventListener('click', () => {
    keepPanelAlive();
    opts.onTestBell();
  });
  adminBtn.addEventListener('click', () => {
    closePanel();
    opts.onOpenAdmin();
  });
  signOutBtn.addEventListener('click', () => {
    closePanel();
    opts.onSignOut();
  });

  // The gesture that unlocks the day's audio. It must be a real touch, so the
  // handler stays synchronous and hands straight off to main.ts.
  arm.addEventListener('click', () => opts.onArmTap());
  arm.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') opts.onArmTap();
  });

  // ------------------------------------------------------------ the update
  // Last written value per element. Comparing here means a tick that changes
  // nothing touches the DOM not at all.
  let lastMinute = -1;
  let lastTime = '';
  let lastPeriod = '';
  let lastPeriodShown: boolean | null = null;
  let lastEyebrow = true;
  let lastEyebrowText = 'Next bell';
  let lastValue = '';
  let lastLeftShown: boolean | null = null;
  let lastBell = '';
  let lastBellShown: boolean | null = null;
  let lastCount = '';
  let lastNote = '';
  let lastWho = '';
  let lastArmed: boolean | null = null;
  let lastOnline: boolean | null = null;
  let lastTrusted: boolean | null = null;

  function update(state: KioskState): void {
    // The clock. Reformatted once a minute, not four times a second.
    const minute = Math.floor(state.nowMs / 60_000);
    if (minute !== lastMinute) {
      lastMinute = minute;
      const text = formatClock(state.nowMs);
      if (text !== lastTime) {
        lastTime = text;
        time.textContent = text;
      }
    }

    // Lower school (K–6): most periods do not ring, so the wall names the next
    // period that *starts* even when it is silent. The next-bell line below
    // stays honest — it only advertises something that actually rings.
    const periodText = state.nextPeriod
      ? `${formatMinutes(state.nextPeriod.start)} · ${state.nextPeriod.label}`
      : '';
    const showPeriod = periodText !== '';
    if (periodText !== lastPeriod) {
      lastPeriod = periodText;
      periodValue.textContent = periodText;
    }
    if (showPeriod !== lastPeriodShown) {
      lastPeriodShown = showPeriod;
      period.hidden = !showPeriod;
    }

    // Primary: the large number is how long THIS period still has, whether or
    // not a bell will ring at the end of it. Upper School, a gap, and dismissal
    // keep the old reading — minutes until the next bell, and nothing else.
    const countingPeriod = state.periodLeftMs != null && state.periodLabel !== '';
    let value = '';
    let eyebrow = true;
    let eyebrowText = 'Next bell';
    if (countingPeriod) {
      eyebrowText = state.periodLabel;
      value = formatUntil(state.periodLeftMs!);
    } else if (state.next) {
      value = formatUntil(state.next.atMs - state.nowMs);
    } else if (state.nextSchoolPlan) {
      // The line says NEXT BELL, so it must name a bell. The first event of a
      // school day is the one-minute warning song before chapel, not a bell —
      // taking events[0] would have every Monday and Friday reading
      // "Tuesday, 8:09 · Arrival" for a band that never rings.
      const first = state.nextSchoolPlan.events.find((e) => e.kind === 'bell');
      if (first) {
        // Minutes are meaningless across a weekend, so this one case says when
        // rather than how long — still without naming the class.
        value = `${weekdayOf(state.nextSchoolPlan.date)}, ${formatMinutes(first.at)}`;
      } else {
        value = 'No bells scheduled';
        eyebrow = false;
      }
    } else {
      value = 'No bells scheduled';
      eyebrow = false;
    }
    if (value !== lastValue) {
      lastValue = value;
      nextValue.textContent = value;
    }
    // "left" sits beside the number, smaller, so the digits stay the thing a
    // teacher reads from across the room. It is only there while the number is
    // the period rather than the next bell.
    if (countingPeriod !== lastLeftShown) {
      lastLeftShown = countingPeriod;
      nextLeft.hidden = !countingPeriod;
    }
    if (eyebrowText !== lastEyebrowText) {
      lastEyebrowText = eyebrowText;
      nextEyebrow.textContent = eyebrowText;
    }
    if (eyebrow !== lastEyebrow) {
      lastEyebrow = eyebrow;
      nextEyebrow.hidden = !eyebrow;
    }

    // While the large line belongs to the period, the next bell moves down
    // here so a silent class end is never advertised as a ring.
    const bellText = countingPeriod && state.next ? formatUntil(state.next.atMs - state.nowMs) : '';
    const showBell = bellText !== '';
    if (bellText !== lastBell) {
      lastBell = bellText;
      bellValue.textContent = bellText;
    }
    if (showBell !== lastBellShown) {
      lastBellShown = showBell;
      bell.hidden = !showBell;
    }

    // The countdown used to live here as a whisper next to the class name. The
    // minutes are now the line itself, so this stays empty rather than saying
    // the same thing twice.
    const count = '';
    if (count !== lastCount) {
      const wasEmpty = lastCount === '';
      lastCount = count;
      if (count === '') nextCount.hidden = true;
      else {
        nextCount.textContent = count;
        if (wasEmpty) nextCount.hidden = false;
      }
    }

    // 'Early dismissal', and nothing louder.
    const noteText = state.note ?? '';
    if (noteText !== lastNote) {
      lastNote = noteText;
      if (noteText === '') note.hidden = true;
      else {
        note.textContent = noteText;
        note.hidden = false;
      }
    }

    // Whose room this tablet is set to — a substitute needs to know.
    const parts = [state.session.name, state.groupName, state.session.roomLabel];
    const whoText = parts.filter((p) => p && p.trim() !== '').join(' · ');
    if (whoText !== lastWho) {
      lastWho = whoText;
      who.textContent = whoText;
    }

    if (state.armed !== lastArmed) {
      lastArmed = state.armed;
      arm.hidden = state.armed;
    }
    if (state.online !== lastOnline) {
      lastOnline = state.online;
      offline.hidden = state.online;
    }
    if (state.clockTrusted !== lastTrusted) {
      lastTrusted = state.clockTrusted;
      syncing.hidden = state.clockTrusted;
    }
  }

  function destroy(): void {
    if (idleTimer !== undefined) window.clearTimeout(idleTimer);
    idleTimer = undefined;
    face.remove();
  }

  return { update, destroy };
}
